import { HTN_REGIMEN_DRUGS } from '../data/rankableElements';

export const HTN_BLISTER_SLOT_COUNT = 29;
export const HTN_OPENING_PILLAR_SLUGS = [
  'lisinopril',
  'amlodipine',
  'chlorthalidone',
];

const SLOT_STATES = new Set(['sealed', 'taken', 'wasted']);
const HTN_DRUG_SLUGS = HTN_REGIMEN_DRUGS.map(([slug]) => slug);

const ICON_PATHS = {
  thiazide: 'M12 4c3 4 5 6.6 5 9a5 5 0 0 1-10 0c0-2.4 2-5 5-9z M9 12.5h6 M9.5 15h5',
  ccb: 'M7.5 5v14 M16.5 5v14 M9.7 13.4h4.6 M12 9.4a1.4 1.4 0 1 0 0.01 0',
  raas: 'M4 12h9 M10.5 9l3 3-3 3 M16.5 7.5v9',
  beta: 'M2 12h4l2-6 3 12 3-9 2 3h6',
};

// TODO: Replace shared class defaults with per-drug copy when the remaining
// blister cards receive authored combat metadata. Names and current combat
// effects below mirror htn_regimen_axes.py; the three opening cards are exact.
const AXIS_DEFAULTS = {
  raas: {
    ability: 'Afterload Ward',
    accent: '#818cf8',
    iconPath: ICON_PATHS.raas,
    desc: 'RAAS blockade raises potassium toward hyperkalemia in regimen combat.',
  },
  dhp: {
    ability: 'Afterload Vent',
    accent: '#fbbf24',
    iconPath: ICON_PATHS.ccb,
    desc: 'Dihydropyridine vasodilation raises pulse via reflex tachycardia.',
  },
  nondhp: {
    ability: 'Nodal Damper',
    accent: '#fbbf24',
    iconPath: ICON_PATHS.ccb,
    desc: 'Non-DHP rate control lowers pulse.',
  },
  beta: {
    ability: 'Adrenergic Brake',
    accent: '#4ade80',
    iconPath: ICON_PATHS.beta,
    desc: 'Cardioselective beta-1 blockade lowers pulse.',
  },
  alphaBeta: {
    // Keep user-facing encounter copy aligned with the canonical Angio roster name.
    ability: 'Alpha-Beta Brake',
    accent: '#4ade80',
    iconPath: ICON_PATHS.beta,
    desc: 'Combined alpha/beta blockade lowers pulse.',
  },
  nitricBeta: {
    ability: 'Nitric Brake',
    accent: '#4ade80',
    iconPath: ICON_PATHS.beta,
    desc: 'Beta-1 blockade with nitric-oxide vasodilation lowers pulse.',
  },
  diuretic: {
    ability: 'Natriuretic Drain',
    accent: '#2dd4bf',
    iconPath: ICON_PATHS.thiazide,
    desc: 'Drains every enemy each round by a flat amount set by the diuretic’s potency. A second drug on the SAME nephron segment adds no drain and still costs potassium; each different segment covered adds a bonus.',
  },
  // Nephron-expansion diuretics (2026-07-25): weak/niche drains on the two
  // remaining nephron segments. Same diuretic mechanic, distinct segment + K copy.
  cai: {
    ability: 'Bicarbonate Drain',
    accent: '#2dd4bf',
    iconPath: ICON_PATHS.thiazide,
    desc: 'Proximal carbonic-anhydrase blockade — a weak flat drain that opens the proximal tubule segment; wastes potassium.',
  },
  osmotic: {
    ability: 'Osmotic Pull',
    accent: '#2dd4bf',
    iconPath: ICON_PATHS.thiazide,
    desc: 'Osmotic diuresis at the descending limb — a modest flat drain, but the water pull retains potassium toward hyperkalemia.',
  },
};

const ACE = new Set(['lisinopril', 'enalapril', 'benazepril']);
const ARB = new Set(['losartan', 'valsartan', 'olmesartan']);
const RAAS = new Set([...ACE, ...ARB]);
const DHP = new Set(['amlodipine', 'nifedipine', 'felodipine']);
const NON_DHP = new Set(['verapamil', 'diltiazem']);
const ALPHA_BETA = new Set(['carvedilol', 'labetalol']);
const BETA = new Set([
  'metoprolol', 'atenolol', 'bisoprolol', 'carvedilol', 'nebivolol',
  'labetalol',
]);
const THIAZIDE = new Set(['hydrochlorothiazide', 'chlorthalidone', 'indapamide']);
const LOOP = new Set(['furosemide', 'torsemide', 'bumetanide']);
const K_SPARING = new Set(['spironolactone', 'eplerenone', 'amiloride', 'triamterene']);
const CARBONIC_ANHYDRASE = new Set(['acetazolamide']);
const OSMOTIC = new Set(['mannitol']);

// ⚠️ MUST MIRROR `AXIS_WEAKEN_BANK_SLUGS` in api/htn.py — NOT `AXIS_BANK_SLUGS`.
// Those two diverged on 2026-07-28 and the difference is load-bearing: the first says
// which banks WEAKEN a warden at spawn (`_htn_warden_start_hp` reduces off it), the
// second says which banks scope that axis's QUESTIONS. This map only decides what the
// tooltip and the prelude chip SAY, so it must track the weaken map. A bank missing
// here does not weaken the effect, it hides it; a bank present here that the engine
// does not apply is worse, because the chip then claims a reduction that never lands.
//
// carbonic_anhydrase_inhibitors + osmotic_diuretics were missing until 2026-07-26,
// added to AXIS_BANK_SLUGS['D'] by the nephron expansion (acetazolamide, mannitol)
// without this map following. The old single combined chip masked it by displaying
// the APPLIED total; per-drug chips made it visible, as an under-count: a loadout
// with all three volume-axis diuretics took 0.75^3 (25 -> 11 HP on Natrix) while the
// prelude claimed a single 25%. This is the same "a new HTN drug needs its frontend
// tables" trap as htn-tree-node-needs-frontend-tables, one table further on.
//
// osmotic_diuretics is now absent BY DECISION, not by that oversight (Edward
// 2026-07-28): mannitol is not an antihypertensive and transiently expands
// intravascular volume before diuresing, so the engine stopped applying it too. It
// keeps its diuresis drain and its Osmotic Bypass card; the change here is what drops
// the weaken row from the drug toast and the chip from the prelude. Re-adding it in
// isolation reintroduces the exact over-claim described above, in reverse.
// 🔴 `export` ADDED 2026-08-15 so the shared-interaction beat can enumerate the
// authored bank -> warden relationship. A module-private const is authored content
// nothing outside can enumerate; adding the keyword changes no call site and no behaviour.
// Memory: `a-private-const-is-unreachable-content`.
export const WARDEN_BY_BANK = {
  ace_inhibitors: 'raas_warden',
  arbs: 'raas_warden',
  calcium_channel_blockers: 'calcium_gate',
  thiazide_diuretics: 'volume_warden',
  loop_diuretics: 'volume_warden',
  carbonic_anhydrase_inhibitors: 'volume_warden',
  potassium_sparing_diuretics: 'aldosterone_lock',
  beta_blockers: 'adrenergic_sentry',
};

const REGEN_CLASSES = new Set(['ace', 'arb', 'dhp_ccb']);
export const HTN_TOOLTIP_REGEN_PER_CARD = 5;
// Per-drug diuresis potency (flat per-round drain), mirroring
// htn_regimen_axes.py::DIURETIC_POTENCY (re-derived 2026-07-26). The diuresis
// mechanic is a FLAT per-round drain set by clinical natriuresis at
// ANTIHYPERTENSIVE dosing — no combat-start seeding, no segment_count**2 ramp,
// no multiplier. Each equipped diuretic's loadout tooltip shows its own potency
// here; the in-combat DiuresisTag shows the aggregate from the wire, which is
// `sum(best agent per covered segment) + 2 * (segments - 1)`. Values differ
// WITHIN a class on purpose. Keep in sync with the backend map.
export const HTN_TOOLTIP_DIURETIC_POTENCY = {
  furosemide: 8, torsemide: 9, bumetanide: 8,               // loop (torsemide: better oral bioavailability)
  chlorthalidone: 5, indapamide: 4, hydrochlorothiazide: 3, // distal thiazide
  spironolactone: 2, eplerenone: 2, amiloride: 2, triamterene: 1, // collecting K-sparing (weak natriuretics; spironolactone earns its slot on the potassium axis, not this one)
  acetazolamide: 2, // proximal carbonic-anhydrase inhibitor (weak alone, additive as adjunct)
  mannitol: 2,      // thin descending-limb osmotic diuretic
};
export const HTN_TOOLTIP_POTASSIUM_RANGE = '3.5–5.2';

// How long a delayed mechanic waits before it starts, in ROUNDS, mirroring the
// `activates_at_round` on the backend's HTN_DRUG_AXIS_TAGS mechanics entries.
//
// Stored as the DELAY (activates_at_round - 1), not as the activation round,
// because that is what a card can honestly state out of combat: a static tooltip
// does not know which round you are in, so "waits 1 round" is true whenever you
// read it while "starts on round 2" is only true before the fight. In combat the
// same chip counts down against the live round.
//
// Two delayed mechanics today: dihydropyridine reflex tachycardia (activates_at_round
// 2, so it waits 1) and the beta class's Remodel (activates_at_round 3, so it waits 2).
// Keep in sync with the backend tags.
export const HTN_MECHANIC_DELAY_ROUNDS = {
  dhp_ccb: { pulse: 1 },
  beta_blocker: { remodel: 2, mortality: 2 },
};

// Reverse cardiac remodelling, mirroring htn_regimen_axes.py::remodel_damage. An extra
// flat per-enemy hit that stays silent for two rounds, opens at 5 on round 3, then
// doubles every round until it caps at 20.
//
// The card states the REGIMEN's schedule, not the card's own share, because Remodel is
// once per regimen: a second beta blocker adds no damage at all. Printing a per-card
// number would promise a stack that does not exist.
export const HTN_REMODEL_OPENING_DAMAGE = 5;
export const HTN_REMODEL_MAX_DAMAGE = 20;

// Mortality Benefit — the player-side heal the three HFrEF beta blockers carry, from
// round 3 onward. Mirrors MORTALITY_BENEFIT_BETA_SLUGS in htn_regimen_axes.py.
//
// ⛔ An EXPLICIT slug set, never derived from `beta_kind`. Atenolol is cardioselective
// exactly like metoprolol and bisoprolol and is NOT one of the three trial drugs, so any
// predicate written against the receptor subtype gets this wrong while looking right.
export const HTN_MORTALITY_BENEFIT_SLUGS = ['metoprolol', 'carvedilol', 'bisoprolol'];
export const HTN_MORTALITY_BENEFIT_HEAL = 5;

// Vasodilation charges, mirroring the `vasodilation_coupon` mechanic in
// htn_regimen_axes.py::HTN_DRUG_AXIS_TAGS. A charge is a COUPON: it voids one whole
// pressure application from an enemy, whatever that application's size — Angio's +1
// and Natrix's +2 each cost exactly one. Charges pool across cards and never
// regenerate inside a fight.
//
// The DHP number is deliberately higher than the alpha-beta number (Edward
// 2026-07-28: "the magnitude and number of vasodilation should be more for the CCBs
// than the beta blockers"). Dihydropyridines are the direct arteriolar dilators;
// carvedilol and labetalol dilate through alpha-1 blockade riding alongside their
// beta effect, so they carry the mechanic but not its full weight.
//
// ⛔ nebivolol is NOT here. Its vasodilation is nitric-oxide mediated rather than
// alpha blockade, which is why its effect is named "Nitric Brake" and not an
// alpha-beta name. Do not widen this to every vasodilating beta blocker.
export const HTN_VASODILATION_CHARGES = {
  dhp_ccb: 3,
  vasodilating_ab: 2,
};

// The charges a single card contributes. Keyed on CLASS for CCBs and on `beta_kind`
// for beta blockers, because within the beta class the receptor subtype is exactly
// what decides whether the card dilates at all.
export function htnVasodilationCharges(meta) {
  if (!meta) return 0;
  if (meta.class === 'dhp_ccb') return HTN_VASODILATION_CHARGES.dhp_ccb;
  if (meta.beta_kind === 'vasodilating_ab') return HTN_VASODILATION_CHARGES.vasodilating_ab;
  return 0;
}

// Per-drug serum-potassium contribution, mirroring the `k_shift` field of
// htn_regimen_axes.py::HTN_DRUG_AXIS_TAGS (re-derived per drug 2026-07-26; it
// used to be a flat +/-1 for a whole class). The SIGN is still uniform inside a
// nephron segment, which is what makes stacking one segment self-punishing; only
// the MAGNITUDE is now graded. Keep in sync with the backend tags.
export const HTN_DRUG_K_SHIFT = {
  // distal thiazide — chlorthalidone is the most hypokalemic (long t1/2)
  hydrochlorothiazide: -2, chlorthalidone: -3, indapamide: -2,
  // loop — kaliuresis from increased distal Na delivery
  furosemide: -2, torsemide: -2, bumetanide: -2,
  // collecting K-sparing. Spironolactone and amiloride retain the most, eplerenone
  // is the weaker MRA, triamterene the weaker ENaC blocker.
  spironolactone: 3, eplerenone: 2, amiloride: 3, triamterene: 1,
  // proximal / descending
  acetazolamide: -1, mannitol: 1,
};

// Frontend mirror of htn_regimen_axes.py::HTN_DRUG_AXIS_TAGS. It extends the
// existing blister metadata used by the full 27-card roster, so the tooltip
// resolver and the HTN map read one FE source rather than competing maps.
//
// 🔴 `export` ADDED 2026-08-11, and it is the whole change to this file. The
// compendium splits the 29-drug HTN folder by real drug class, and this is where
// that class is stated -- a module-private function is authored content nothing
// outside can enumerate, which is the same gap `DRUG_ICON_PATHS`,
// `KEYSTONE_BEAT_DESC` and `STATUS_WORDS` each closed the same way. Adding the
// keyword changes no call site and no behaviour; the body below is untouched.
// Memory: `a-private-const-is-unreachable-content`.
export function axisTagsFor(slug) {
  if (ACE.has(slug)) {
    return { axis: 'raas', class: 'ace', k_shift: 2, bank_slug: 'ace_inhibitors' };
  }
  if (ARB.has(slug)) {
    return { axis: 'raas', class: 'arb', k_shift: 2, bank_slug: 'arbs' };
  }
  if (DHP.has(slug)) {
    return {
      axis: 'calcium', class: 'dhp_ccb', pulse_shift: 1,
      bank_slug: 'calcium_channel_blockers',
    };
  }
  if (NON_DHP.has(slug)) {
    return {
      axis: 'calcium', class: 'nondhp_ccb', pulse_shift: -1,
      bank_slug: 'calcium_channel_blockers',
    };
  }
  if (BETA.has(slug)) {
    const betaKind = ALPHA_BETA.has(slug)
      ? 'vasodilating_ab'
      : slug === 'nebivolol'
        ? 'vasodilating_no'
        : 'cardioselective';
    return {
      axis: 'sympathetic', class: 'beta_blocker', beta_kind: betaKind,
      pulse_shift: -1, bank_slug: 'beta_blockers',
    };
  }
  if (THIAZIDE.has(slug)) {
    return {
      axis: 'diuretic', class: 'thiazide', nephron_segment: 'distal',
      k_shift: HTN_DRUG_K_SHIFT[slug] ?? -2, bank_slug: 'thiazide_diuretics',
    };
  }
  if (LOOP.has(slug)) {
    return {
      axis: 'diuretic', class: 'loop', nephron_segment: 'loop',
      k_shift: HTN_DRUG_K_SHIFT[slug] ?? -2, bank_slug: 'loop_diuretics',
    };
  }
  if (K_SPARING.has(slug)) {
    return {
      axis: 'diuretic', class: 'k_sparing_mra', nephron_segment: 'collecting',
      k_shift: HTN_DRUG_K_SHIFT[slug] ?? 2, bank_slug: 'potassium_sparing_diuretics',
    };
  }
  if (CARBONIC_ANHYDRASE.has(slug)) {
    return {
      axis: 'diuretic', class: 'carbonic_anhydrase', nephron_segment: 'proximal',
      k_shift: HTN_DRUG_K_SHIFT[slug] ?? -1, bank_slug: 'carbonic_anhydrase_inhibitors',
    };
  }
  if (OSMOTIC.has(slug)) {
    return {
      axis: 'diuretic', class: 'osmotic', nephron_segment: 'descending',
      k_shift: HTN_DRUG_K_SHIFT[slug] ?? 1, bank_slug: 'osmotic_diuretics',
    };
  }
  return null;
}

// A card that carries a MAIN-GAME effect alongside its HTN one has a single name, and it
// is the CARD's name, not the effect's. Owner ruling 2026-07-26: "Osmotic Bypass IS THE
// CARD EFFECT NAME, THE SINGLE NAME ... an individual card can have multiple effects. I
// want mannitol to retain its original main game effect + have a diuretic effect ... the
// same version both places."
//
// Without this, mannitol is labelled from its diuretic CLASS ("Osmotic Pull") and reads as
// a different card from the "Osmotic Bypass" the player bought on the map. The override is
// per-DRUG on purpose: the shared `osmotic` class entry still serves acetazolamide and any
// future osmotic diuretic, which genuinely are their diuretic effect and nothing else.
// Mirrors HTN_DRUG_AXIS_TAGS[slug].reservoir_effect_id in htn_regimen_axes.py.
const CARD_NAME_OVERRIDES = {
  mannitol: 'Osmotic Bypass',
};

function defaultMetaFor(slug) {
  if (RAAS.has(slug)) return AXIS_DEFAULTS.raas;
  if (DHP.has(slug)) return AXIS_DEFAULTS.dhp;
  if (NON_DHP.has(slug)) return AXIS_DEFAULTS.nondhp;
  if (ALPHA_BETA.has(slug)) return AXIS_DEFAULTS.alphaBeta;
  if (slug === 'nebivolol') return AXIS_DEFAULTS.nitricBeta;
  if (['metoprolol', 'atenolol', 'bisoprolol'].includes(slug)) return AXIS_DEFAULTS.beta;
  if (CARBONIC_ANHYDRASE.has(slug)) return AXIS_DEFAULTS.cai;
  if (OSMOTIC.has(slug)) return AXIS_DEFAULTS.osmotic;
  return AXIS_DEFAULTS.diuretic;
}

export const HTN_DRUG_META = Object.fromEntries(
  HTN_REGIMEN_DRUGS.map(([slug, name]) => [slug, {
    name,
    ...defaultMetaFor(slug),
    ...axisTagsFor(slug),
    // Applied LAST so the card's own name wins over the class default it spreads in
    // above. Only the label changes; accent, icon and copy stay with the class, because
    // the drug really does do the class's diuretic thing.
    ...(CARD_NAME_OVERRIDES[slug] ? { ability: CARD_NAME_OVERRIDES[slug] } : {}),
  }]),
);

// Exported (2026-07-25) so a surface rendering `drugInfluenceRows(card)` can resolve
// the SAME slug for the rows' embedded status tooltips. Sharing the resolver is what
// makes it impossible for a row and its tooltip to describe different drugs.
export function normalizeDrugSlug(cardOrSlug) {
  const raw = typeof cardOrSlug === 'string'
    ? cardOrSlug
    : cardOrSlug?.drug_slug
      || cardOrSlug?.slug
      || cardOrSlug?.card_slug
      || cardOrSlug?.generic_name
      || '';
  return String(raw).toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// AV BLOCK (2026-07-28). Mirrors `AV_BLOCK_CLASS_PAIR` in htn_regimen_axes.py: a
// beta blocker equipped alongside a non-DHP CCB blocks AV conduction, and the
// engine collapses the round to a single beat.
//
// ⛔ This lives on the CARD deliberately. A pair rule the player cannot read while
// drafting is a rule that only ever punishes, which is exactly the content gap
// recorded for the diuretic synergy rule. The partner is named, not the magnitude:
// the number belongs to the rail, which renders it from the engine payload.
export const HTN_AV_BLOCK_PARTNER_COPY = {
  beta_blocker: 'Pairs badly with non-DHP CCBs',
  nondhp_ccb: 'Pairs badly with beta blockers',
};

export function htnAvBlockClass(cardOrSlug) {
  const slug = normalizeDrugSlug(cardOrSlug);
  if (BETA.has(slug)) return 'beta_blocker';
  if (NON_DHP.has(slug)) return 'nondhp_ccb';
  return null;
}

export function htnAvBlockPartnerCopy(cardOrSlug) {
  return HTN_AV_BLOCK_PARTNER_COPY[htnAvBlockClass(cardOrSlug)] || '';
}

// Class/axis-driven authored rows for a drug tooltip. Constants deliberately
// mirror locked backend values; tests pin the three starter arrangements and
// the roster metadata so display drift is caught without changing mechanics.
export function drugInfluenceRows(cardOrSlug) {
  const slug = normalizeDrugSlug(cardOrSlug);
  const meta = HTN_DRUG_META[slug];
  if (!meta) return [];

  const rows = [];
  if (meta.axis === 'diuretic') {
    // Flat per-round drain by clinical potency (no combat-start seeding).
    rows.push({
      type: 'inflicts',
      status: 'diuresis',
      perRound: HTN_TOOLTIP_DIURETIC_POTENCY[slug] ?? 1,
    });
  }
  if (meta.class === 'beta_blocker') {
    // Beside the diuresis drain, deliberately: both are flat per-enemy hits the
    // regimen lands every round, so they read as the same kind of claim. Every
    // draftable beta carries it (propranolol, the one beta with a unique numeric
    // effect, is excluded from the draft pool and has no entry in HTN_DRUG_META).
    rows.push({
      type: 'inflicts',
      status: 'remodel',
      opening: HTN_REMODEL_OPENING_DAMAGE,
      max: HTN_REMODEL_MAX_DAMAGE,
      delayRounds: HTN_MECHANIC_DELAY_ROUNDS[meta.class]?.remodel || 0,
    });
  }
  if (HTN_MORTALITY_BENEFIT_SLUGS.includes(slug)) {
    // Sits where Adherence sits, and can never collide with it: the Adherence classes
    // are ACE / ARB / DHP CCB, so no card carries both heals.
    rows.push({
      type: 'mortality-benefit',
      amount: HTN_MORTALITY_BENEFIT_HEAL,
      cadence: 'round',
      delayRounds: HTN_MECHANIC_DELAY_ROUNDS[meta.class]?.mortality || 0,
    });
  }
  if (REGEN_CLASSES.has(meta.class)) {
    rows.push({ type: 'regen', amount: HTN_TOOLTIP_REGEN_PER_CARD, cadence: 'round' });
  }

  const strongWarden = WARDEN_BY_BANK[meta.bank_slug];
  if (strongWarden) {
    rows.push({ type: 'weaken', strength: 'strong', warden: strongWarden });
  }
  if (meta.class === 'ace' || meta.class === 'arb') {
    rows.push({ type: 'weaken', strength: 'weak', warden: 'calcium_gate' });
  } else if (meta.class === 'dhp_ccb') {
    rows.push({ type: 'weaken', strength: 'weak', warden: 'raas_warden' });
  }

  if (Number(meta.k_shift || 0) !== 0) {
    rows.push({
      type: 'influences-potassium',
      range: HTN_TOOLTIP_POTASSIUM_RANGE,
      line: 'chance to take damage on wrong answer',
    });
  }
  if (Number(meta.pulse_shift || 0) !== 0) {
    rows.push({
      type: 'influences-pulse',
      qualifier: meta.pulse_shift > 0 ? 'compensatory' : 'rate control',
      // 0 for every rate-control drug, so the row is byte-identical to before
      // for beta blockers and non-DHP CCBs. Only the reflex-tachycardia branch
      // carries a delay today.
      delayRounds: HTN_MECHANIC_DELAY_ROUNDS[meta.class]?.pulse || 0,
    });
  }
  const vasodilationCharges = htnVasodilationCharges(meta);
  if (vasodilationCharges > 0) {
    // Last, deliberately. Vasodilation is a defensive coupon against what the enemy
    // does, so it reads after the card's own offensive and physiological rows.
    rows.push({ type: 'grants', status: 'vasodilation', charges: vasodilationCharges });
  }
  return rows;
}

// Pharmacology-axis accent for a drug tooltip: indigo RAAS (#818cf8), amber CCB
// (#fbbf24), teal diuretic (#2dd4bf), etc. This is the accent Edward's authored
// reference cards paint (`--pill-accent` on the hero Lisinopril/Amlodipine/
// Chlorthalidone cards == these axis values), distinct from the skill-tree
// identity accent (gold-default). Returns null for non-HTN-regimen drugs so the
// caller falls back to the tree accent. Detection is keyed on the same
// HTN_DRUG_META lookup drugInfluenceRows uses, so the accent applies to exactly
// the set of toasts that render the authored influence rows — they never drift.
export function htnAxisAccent(cardOrSlug) {
  const meta = HTN_DRUG_META[normalizeDrugSlug(cardOrSlug)];
  return meta?.accent || null;
}

export function seedBlisterState() {
  return {
    slots: Object.fromEntries(HTN_DRUG_SLUGS.map((slug) => [slug, 'sealed'])),
    pending: null,
    opensDone: 0,
  };
}

export function seedBlisterStateFromOpening(winnerSlug) {
  return reconcileBlisterStateFromOpening(seedBlisterState(), winnerSlug);
}

export function reconcileBlisterStateFromOpening(state, winnerSlug) {
  if (
    !HTN_OPENING_PILLAR_SLUGS.includes(winnerSlug)
    || !state
    || typeof state !== 'object'
    || !state.slots
    || typeof state.slots !== 'object'
  ) {
    return state;
  }

  let changed = false;
  const slots = { ...state.slots };
  HTN_OPENING_PILLAR_SLUGS.forEach((slug) => {
    if (slots[slug] !== 'sealed') return;
    slots[slug] = slug === winnerSlug ? 'taken' : 'wasted';
    changed = true;
  });

  let pending = state.pending;
  if (Array.isArray(pending)) {
    const sanitized = pending.filter((slug) => !HTN_OPENING_PILLAR_SLUGS.includes(slug));
    if (sanitized.length !== pending.length) {
      pending = sanitized.length > 0 ? sanitized : null;
      changed = true;
    }
  }

  const opensDone = state.opensDone === 0 ? 1 : state.opensDone;
  if (opensDone !== state.opensDone) changed = true;
  if (!changed) return state;
  return { ...state, slots, pending, opensDone };
}

export function blisterStorageKey(owner) {
  return `htn_blister:${owner}`;
}

export function isValidBlisterState(state) {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return false;
  if (!state.slots || typeof state.slots !== 'object' || Array.isArray(state.slots)) return false;
  const slotKeys = Object.keys(state.slots);
  if (slotKeys.length !== HTN_BLISTER_SLOT_COUNT) return false;
  if (!HTN_DRUG_SLUGS.every((slug) => SLOT_STATES.has(state.slots[slug]))) return false;
  if (!Number.isInteger(state.opensDone) || state.opensDone < 0) return false;
  if (state.pending === null) return true;
  if (!Array.isArray(state.pending) || state.pending.length > 3) return false;
  if (new Set(state.pending).size !== state.pending.length) return false;
  return state.pending.every((slug) => state.slots[slug] === 'sealed');
}

// Forward-compat migration (2026-07-25): when the roster gains drugs (e.g. the
// nephron-expansion acetazolamide / mannitol bumped the slot count 27 -> 29), a
// blister state persisted under the OLD count is missing their slots and would be
// rejected by isValidBlisterState -> silently reseeded, discarding the player's
// draft progress. Instead, PAD each missing current slug as 'sealed': the player
// keeps every taken/wasted slot AND the new cards become drawable. Only pads (never
// prunes); if the padded result still isn't valid (unknown-shape / removed slugs),
// the caller falls back to a fresh seed. Returns null when the input isn't a
// recognizable blister object.
export function migrateBlisterState(state) {
  if (
    !state || typeof state !== 'object' || Array.isArray(state)
    || !state.slots || typeof state.slots !== 'object' || Array.isArray(state.slots)
  ) {
    return null;
  }
  let changed = false;
  const slots = { ...state.slots };
  HTN_DRUG_SLUGS.forEach((slug) => {
    if (!(slug in slots)) {
      slots[slug] = 'sealed';
      changed = true;
    }
  });
  const migrated = changed ? { ...state, slots } : state;
  return isValidBlisterState(migrated) ? migrated : null;
}

export function readBlisterState(owner) {
  if (typeof window === 'undefined' || !owner) return seedBlisterState();
  try {
    const state = JSON.parse(window.localStorage.getItem(blisterStorageKey(owner)) || 'null');
    if (isValidBlisterState(state)) return state;
    // A pre-upgrade state (missing newly-added drug slots) is migrated in place
    // rather than discarded, preserving the player's draft progress.
    return migrateBlisterState(state) || seedBlisterState();
  } catch {
    return seedBlisterState();
  }
}

export function writeBlisterState(owner, state) {
  const safeState = isValidBlisterState(state) ? state : seedBlisterState();
  if (typeof window === 'undefined' || !owner) return safeState;
  try {
    window.localStorage.setItem(blisterStorageKey(owner), JSON.stringify(safeState));
  } catch (_) { /* best-effort device-local persistence */ }
  return safeState;
}

export function drawPending(state, rng = Math.random) {
  if (state.pending !== null) return state;
  const available = Object.keys(state.slots).filter((slug) => state.slots[slug] === 'sealed');
  const pending = [];
  const drawCount = Math.min(3, available.length);
  while (pending.length < drawCount) {
    const rawIndex = Math.floor(rng() * available.length);
    const index = Math.max(0, Math.min(available.length - 1, rawIndex));
    pending.push(available.splice(index, 1)[0]);
  }
  return { ...state, pending };
}

export function commitPick(state, slug) {
  if (!Array.isArray(state.pending) || !state.pending.includes(slug)) return state;
  const pending = new Set(state.pending);
  const slots = Object.fromEntries(Object.entries(state.slots).map(([slotSlug, slotState]) => {
    if (!pending.has(slotSlug)) return [slotSlug, slotState];
    return [slotSlug, slotSlug === slug ? 'taken' : 'wasted'];
  }));
  return { ...state, slots, pending: null, opensDone: state.opensDone + 1 };
}

// Skip the whole reward: take NONE. Every pending slug is spent (wasted) exactly
// like the two you pass on when you DO draft (Edward 2026-07-20 — a skip must not
// let players fish blister-after-blister for a good trio), and the open still
// counts (opensDone++). No 'taken' slot, so the caller equips nothing.
export function commitSkip(state) {
  if (!Array.isArray(state.pending) || state.pending.length === 0) return state;
  const pending = new Set(state.pending);
  const slots = Object.fromEntries(Object.entries(state.slots).map(([slotSlug, slotState]) => (
    pending.has(slotSlug) ? [slotSlug, 'wasted'] : [slotSlug, slotState]
  )));
  return { ...state, slots, pending: null, opensDone: state.opensDone + 1 };
}

export function sealedCount(state) {
  return Object.values(state.slots).filter((slotState) => slotState === 'sealed').length;
}

export function takenSlugs(state) {
  return Object.keys(state.slots).filter((slug) => state.slots[slug] === 'taken');
}

export function wastedSlugs(state) {
  return Object.keys(state.slots).filter((slug) => state.slots[slug] === 'wasted');
}
